import { useCallback, useEffect, useState } from 'react'
import {
  ApiHatasi,
  api,
  oynaticiAdresi,
  type DanisanDosyasi,
  type OlcumOzet,
  type OnamDurumu,
  type OdevOzet,
  type RaporHedefi,
  type RaporOzet,
  type YeniOnam,
} from '../api'
import {
  egzersizAdi,
  HEDEF_ADI,
  YAKINLIK_ADI,
  haftaAdresi,
  haftaApi,
  haftaBaskiAdresi,
  veliAdresi,
} from '../api'
import type { GorevSatiri, SeansOzet } from '../api'
import { SEANS_TURU, YAKINLIK_ADI as YAKINLIK } from '../api'
import type { YeniKisi } from '../api'
import { Baglanti } from '../Baglanti'
import { DosyaOkul } from './DosyaOkul'
import { DosyaGozlem } from './DosyaGozlem'
import { DosyaEkler, OnamKaniti } from './DosyaEkler'
import { DosyaDavranis } from './DosyaDavranis'

const KAPSAM_ADI: Record<string, string> = {
  data_processing: 'Aydınlatma ve veri işleme',
  assessment: 'Ölçüm',
  program: 'Egzersiz programı',
  parent_portal: 'Veli portalı',
  school_sharing: 'Okulla paylaşım',
  research: 'Araştırma',
}

/**
 * Onam belgesinin kodu ve sürümü.
 *
 * Metnin hangi sürümünün imzalandığı kayda geçmek zorunda: metin
 * değişince eski imza yeni metni kapsamaz. Burada sabit tutuluyor,
 * ileride belge yönetimi gelirse oradan okunacak.
 */
const BELGE: Record<string, { kod: string; surum: string }> = {
  data_processing: { kod: 'KVKK-AYDINLATMA', surum: '1.0' },
  assessment: { kod: 'OLCUM-ONAM', surum: '1.0' },
  program: { kod: 'PROGRAM-ONAM', surum: '1.0' },
  parent_portal: { kod: 'VELI-PORTAL-ONAM', surum: '1.0' },
  school_sharing: { kod: 'OKUL-PAYLASIM-ONAM', surum: '1.0' },
}

const DURUM_ADI: Record<string, string> = {
  in_progress: 'Sürüyor',
  completed: 'Tamamlandı',
  abandoned: 'Yarıda kaldı',
  invalidated: 'Geçersiz',
}

function tarih(s: string | null): string {
  if (!s) return '—'
  const d = new Date(s)
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleString('tr-TR', { dateStyle: 'short', timeStyle: 'short' })
}

/** Yalnızca gün: dönem tarihlerinde saat göstermenin anlamı yok */
function gun(s: string | null): string {
  if (!s) return '—'
  const d = new Date(s)
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('tr-TR', { dateStyle: 'medium' })
}

export function Danisan({
  id,
  yazabilir,
  onGeri,
  onRapor,
  onRaporTaslagi,
  onOdev,
  onProgram,
  onOdevAta,
  onSeans,
  onHesap,
}: {
  id: string
  yazabilir: boolean
  onGeri: () => void
  onRapor: (olcumId: string) => void
  onRaporTaslagi: (raporId: string) => void
  onOdev: (odevId: string) => void
  onProgram: () => void
  onOdevAta: () => void
  onSeans: (seansId: string) => void
  onHesap: () => void
}) {
  const [dosya, setDosya] = useState<DanisanDosyasi | null>(null)
  const [onam, setOnam] = useState<OnamDurumu | null>(null)
  const [olcumler, setOlcumler] = useState<OlcumOzet[]>([])
  const [hata, setHata] = useState<string | null>(null)

  const yukle = useCallback(async () => {
    setHata(null)
    try {
      const [d, o, g] = await Promise.all([
        api.danisan(id),
        api.onamDurumu(id),
        api.olcumGecmisi(id),
      ])
      setDosya(d)
      setOnam(o)
      setOlcumler(g.olcumler)
    } catch {
      setHata('Dosya açılamadı.')
    }
  }, [id])

  useEffect(() => {
    void yukle()
  }, [yukle])

  if (hata) {
    return (
      <>
        <div className="uyari">{hata}</div>
        <button onClick={onGeri}>← Danışanlar</button>
      </>
    )
  }
  if (!dosya || !onam) return <p className="alt">Yükleniyor…</p>

  const d = dosya.danisan as Record<string, string | null>

  return (
    <>
      <div className="dugmeler" style={{ marginBottom: 12 }}>
        <button onClick={onGeri}>← Danışanlar</button>
      </div>

      <h1>
        {d['firstName']} {d['lastName']}
      </h1>
      <p className="alt">
        Dosya no {d['fileNo']} · Doğum {d['birthDate']}
        {d['school'] ? ` · ${d['school']}` : ''}
        {d['grade'] ? ` ${d['grade']}` : ''}
        {d['phone'] ? ` · ${d['phone']}` : ''}
      </p>

      {yazabilir && (
        <DosyaDuzenle danisanId={id} dosya={d} onKayit={() => void yukle()} />
      )}

      {d['intakeSummary'] && (
        <div className="kart">
          <strong style={{ fontSize: 13, color: 'var(--muted)' }}>Ön değerlendirme</strong>
          <p style={{ margin: '6px 0 0', whiteSpace: 'pre-wrap' }}>{d['intakeSummary']}</p>
        </div>
      )}

      <Onamlar
        dosya={dosya}
        onam={onam}
        yazabilir={yazabilir}
        danisanId={id}
        onDegisti={() => void yukle()}
      />

      <OlcumBaslat
        danisanId={id}
        acik={onam.olcumBaslatilabilir}
        yazabilir={yazabilir}
        onBasladi={() => void yukle()}
      />

      <div className="dugmeler" style={{ marginBottom: 14 }}>
        <button onClick={onHesap}>Paket ve ödeme</button>
      </div>

      <Kisiler
        danisanId={id}
        kisiler={dosya.ilgiliKisiler as Record<string, string>[]}
        yazabilir={yazabilir}
        portalOnami={onam.veliPortali}
        onDegisti={() => void yukle()}
      />

      <DosyaOkul
        danisanId={id}
        yazabilir={yazabilir}
        okulOnami={onam.okulPaylasimi}
        onDegisti={() => void yukle()}
      />

      <DosyaGozlem
        danisanId={id}
        yazabilir={yazabilir}
        kisiler={(dosya.ilgiliKisiler as Record<string, string>[]).map((k) => ({
          id: String(k['id']),
          adSoyad: String(k['fullName'] ?? ''),
        }))}
      />

      <DosyaDavranis danisanId={id} yazabilir={yazabilir} />

      <DosyaEkler danisanId={id} yazabilir={yazabilir} onDegisti={() => void yukle()} />

      <Seanslar danisanId={id} yazabilir={yazabilir} onAc={onSeans} />

      <Odevler
        danisanId={id}
        onAc={onOdev}
        yazabilir={yazabilir}
        onProgram={onProgram}
        onOdevAta={onOdevAta}
      />

      <Raporlar danisanId={id} yazabilir={yazabilir} onAc={onRaporTaslagi} />

      <h2>Ölçümler</h2>
      {olcumler.length === 0 ? (
        <div className="bilgi">Bu dosyada henüz ölçüm yok.</div>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Başlangıç</th>
              <th>Form</th>
              <th>Durum</th>
              <th>Not</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {olcumler.map((o) => (
              <tr key={o.id}>
                <td>{tarih(o.baslangic)}</td>
                <td>{o.form}</td>
                <td>
                  <span
                    className={
                      'rozet' +
                      (o.durum === 'completed' ? ' ok' : o.durum === 'in_progress' ? ' warn' : '')
                    }
                  >
                    {DURUM_ADI[o.durum] ?? o.durum}
                  </span>
                </td>
                <td style={{ color: o.uyari ? 'var(--bad)' : 'var(--muted)', fontSize: 13 }}>
                  {o.uyari ? 'Geçerlilik uyarısı' : '—'}
                </td>
                <td style={{ textAlign: 'right' }}>
                  {o.durum === 'completed' ? (
                    <button onClick={() => onRapor(o.id)}>Rapor</button>
                  ) : (
                    <span className="alt" style={{ margin: 0 }}>
                      tamamlanmadı
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  )
}

/* ------------------------------------------------------------------ */

/* ------------------------------------------------------------------ */

/**
 * Dosya düzenleme.
 *
 * Dosya açıldıktan sonra telefon, okul, sınıf değişiyor ve değişmesi
 * gerekiyor. Bu ekran yokken yanlış girilen bir telefonu düzeltmenin
 * hiçbir yolu yoktu.
 */
function DosyaDuzenle({
  danisanId,
  dosya,
  onKayit,
}: {
  danisanId: string
  dosya: Record<string, string | null>
  onKayit: () => void
}) {
  const [acik, setAcik] = useState(false)
  const [f, setF] = useState<Record<string, string>>({})
  const [hata, setHata] = useState<string | null>(null)
  const [bekliyor, setBekliyor] = useState(false)

  const ac = () => {
    setF({
      dosyaNo: dosya['fileNo'] ?? '',
      ad: dosya['firstName'] ?? '',
      soyad: dosya['lastName'] ?? '',
      dogumTarihi: (dosya['birthDate'] ?? '').slice(0, 10),
      telefon: dosya['phone'] ?? '',
      okul: dosya['school'] ?? '',
      sinif: dosya['grade'] ?? '',
      yonlendiren: dosya['referredBy'] ?? '',
      onDegerlendirme: dosya['intakeSummary'] ?? '',
    })
    setAcik(true)
  }

  const kaydet = async () => {
    setHata(null)
    setBekliyor(true)
    try {
      await api.danisanGuncelle(danisanId, f)
      setAcik(false)
      onKayit()
    } catch (e) {
      setHata(
        e instanceof ApiHatasi && e.kod === 'dosya_no_kullanimda'
          ? 'Bu dosya numarası başka bir danışanda kullanılıyor.'
          : 'Kaydedilemedi.',
      )
    } finally {
      setBekliyor(false)
    }
  }

  if (!acik) {
    return (
      <div className="dugmeler" style={{ marginBottom: 14 }}>
        <button onClick={ac}>Dosyayı düzenle</button>
      </div>
    )
  }

  const alan = (anahtar: string, etiket: string, tip = 'text') => (
    <label key={anahtar}>
      {etiket}
      <input
        type={tip}
        value={f[anahtar] ?? ''}
        onChange={(e) => setF({ ...f, [anahtar]: e.target.value })}
      />
    </label>
  )

  return (
    <div className="kart">
      <h2 style={{ marginTop: 0 }}>Dosyayı düzenle</h2>
      {hata && <div className="uyari">{hata}</div>}
      <div className="satir">
        {alan('ad', 'Ad')}
        {alan('soyad', 'Soyad')}
        {alan('dogumTarihi', 'Doğum tarihi', 'date')}
        {alan('dosyaNo', 'Dosya no')}
        {alan('telefon', 'Telefon')}
        {alan('okul', 'Okul')}
        {alan('sinif', 'Sınıf')}
        {alan('yonlendiren', 'Yönlendiren')}
      </div>
      <label>
        Ön değerlendirme
        <textarea
          rows={3}
          value={f['onDegerlendirme'] ?? ''}
          onChange={(e) => setF({ ...f, onDegerlendirme: e.target.value })}
        />
      </label>
      <div className="dugmeler">
        <button className="birincil" disabled={bekliyor} onClick={() => void kaydet()}>
          {bekliyor ? 'Kaydediliyor…' : 'Kaydet'}
        </button>
        <button onClick={() => setAcik(false)} disabled={bekliyor}>
          Vazgeç
        </button>
      </div>
      <p className="alt" style={{ margin: '10px 0 0' }}>
        Doğum tarihi ve dosya no değişiklikleri denetim kaydına düşer.
      </p>
    </div>
  )
}

/**
 * İlgili kişiler: veli, vasi, öğretmen.
 *
 * Veli portalı, rapor gönderimi ve onamı veren kişi hep buraya bağlı.
 * Portal erişimini açmak tek başına yetmiyor: veli portalı ayrıca
 * VELİ PORTALI ONAMI arıyor. İkisi ayrı kararlar — biri teknik
 * erişim, öbürü rıza.
 */
function Kisiler({
  danisanId,
  kisiler,
  yazabilir,
  portalOnami,
  onDegisti,
}: {
  danisanId: string
  kisiler: Record<string, string>[]
  yazabilir: boolean
  /** Veli portalı onamı alınmış mı — bağlantı üretmenin şartı */
  portalOnami: boolean
  onDegisti: () => void
}) {
  const [formAcik, setFormAcik] = useState(false)
  const [uretilen, setUretilen] = useState<{ kisiId: string; adres: string; saat: number } | null>(
    null,
  )
  const [bekleyen, setBekleyen] = useState<string | null>(null)
  const [y, setY] = useState<YeniKisi>({
    yakinlik: 'mother',
    adSoyad: '',
    telefon: '',
    eposta: '',
    portal: true,
    yasalTemsilci: true,
  })
  const [hata, setHata] = useState<string | null>(null)
  const [bekliyor, setBekliyor] = useState(false)

  const ekle = async (e: React.FormEvent) => {
    e.preventDefault()
    setHata(null)
    setBekliyor(true)
    try {
      await api.kisiEkle(danisanId, {
        ...y,
        telefon: y.telefon?.trim() || null,
        eposta: y.eposta?.trim() || null,
      })
      setY({ yakinlik: 'mother', adSoyad: '', telefon: '', eposta: '', portal: true, yasalTemsilci: true })
      setFormAcik(false)
      onDegisti()
    } catch {
      setHata('Kişi eklenemedi. E-posta biçimini kontrol edin.')
    } finally {
      setBekliyor(false)
    }
  }

  const degistir = async (id: string, g: Record<string, unknown>) => {
    try {
      await api.kisiGuncelle(id, g)
      onDegisti()
    } catch {
      setHata('Güncellenemedi.')
    }
  }

  /**
   * Tek kullanımlık portal bağlantısı.
   *
   * Veli kayıtlı e-postasına kod isteyerek de girebiliyor; bu yol
   * e-postası olmayan ya da ulaşamayan veli için. Kimliği sistem
   * değil UYGULAYICI doğrulamış oluyor, o yüzden kimin ürettiği
   * denetime yazılıyor.
   */
  const baglantiUret = async (kisiId: string) => {
    setHata(null)
    setBekleyen(kisiId)
    try {
      const r = await api.veliBaglantisi(danisanId, kisiId)
      setUretilen({
        kisiId,
        adres: veliAdresi(r.jeton),
        saat: Math.round(r.gecerlilikSn / 3600),
      })
    } catch (e) {
      const k = e instanceof ApiHatasi ? e.kod : ''
      setHata(
        k === 'onam_yok'
          ? 'Veli portalı onamı alınmadan bağlantı üretilemiyor.'
          : k === 'portal_kapali'
            ? 'Bu kişinin portal erişimi kapalı.'
            : 'Bağlantı üretilemedi.',
      )
    } finally {
      setBekleyen(null)
    }
  }

  return (
    <div className="kart">
      <h2 style={{ margin: '0 0 4px' }}>İlgili kişiler</h2>
      <p className="alt" style={{ marginBottom: 12 }}>
        Veli portalı, rapor gönderimi ve onam hep buradaki kişilere bağlı.
      </p>
      {hata && <div className="uyari">{hata}</div>}

      {kisiler.length === 0 ? (
        <div className="bilgi">
          Kayıtlı kişi yok. Veli portalını kullanmak ve rapor göndermek için en az bir kişi
          eklenmeli.
        </div>
      ) : (
        kisiler.map((k) => (
          <div
            key={k['id']}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '10px 0',
              borderTop: '1px solid var(--line)',
              flexWrap: 'wrap',
            }}
          >
            <span style={{ flex: 1, minWidth: 180 }}>
              <strong>{k['fullName']}</strong>
              <div className="alt" style={{ margin: 0, fontSize: 12 }}>
                {YAKINLIK[k['relation'] ?? ''] ?? k['relation']}
                {k['phone'] ? ` · ${k['phone']}` : ''}
                {k['email'] ? ` · ${k['email']}` : ''}
              </div>
            </span>
            {k['isLegalGuardian'] && <span className="rozet">yasal temsilci</span>}
            <span className={'rozet' + (k['portalEnabled'] ? ' ok' : '')}>
              {k['portalEnabled'] ? 'Portal açık' : 'Portal kapalı'}
            </span>
            {yazabilir && (
              <>
                <button
                  onClick={() => void degistir(String(k['id']), { portal: !k['portalEnabled'] })}
                >
                  {k['portalEnabled'] ? 'Portalı kapat' : 'Portalı aç'}
                </button>
                <button
                  disabled={!portalOnami || !k['portalEnabled'] || bekleyen === k['id']}
                  title={
                    !portalOnami
                      ? 'Önce veli portalı onamı kaydedilmeli'
                      : 'Tek kullanımlık bağlantı üretir'
                  }
                  onClick={() => void baglantiUret(String(k['id']))}
                >
                  {bekleyen === k['id'] ? 'Üretiliyor…' : 'Bağlantı üret'}
                </button>
                <button onClick={() => void degistir(String(k['id']), { arsiv: true })}>
                  Kaldır
                </button>
              </>
            )}
            {uretilen !== null && uretilen.kisiId === k['id'] && (
              <div style={{ flexBasis: '100%' }}>
                <Baglanti
                  adres={uretilen.adres}
                  telefon={k['phone']}
                  mesaj={`Merhaba, Nirengi veli portalı için bağlantınız aşağıda. Bir kez açılır, ${uretilen.saat} saat geçerli.`}
                  not={`Bağlantı ${uretilen.saat} saat geçerli ve BİR KEZ açılır. Veli kayıtlı e-postasıyla kod isteyerek de girebilir.`}
                />
              </div>
            )}
          </div>
        ))
      )}

      {yazabilir && !formAcik && (
        <div className="dugmeler">
          <button className="birincil" onClick={() => setFormAcik(true)}>
            Kişi ekle
          </button>
        </div>
      )}

      {formAcik && (
        <form onSubmit={ekle} style={{ borderTop: '1px solid var(--line)', paddingTop: 12 }}>
          <div className="satir">
            <label>
              Yakınlık
              <select
                value={y.yakinlik}
                onChange={(e) => setY({ ...y, yakinlik: e.target.value })}
              >
                {Object.entries(YAKINLIK).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Ad soyad
              <input
                value={y.adSoyad}
                onChange={(e) => setY({ ...y, adSoyad: e.target.value })}
                required
                minLength={2}
              />
            </label>
            <label>
              Telefon
              <input
                value={y.telefon ?? ''}
                onChange={(e) => setY({ ...y, telefon: e.target.value })}
                placeholder="0532 000 00 00"
              />
            </label>
            <label>
              E-posta
              <input
                type="email"
                value={y.eposta ?? ''}
                onChange={(e) => setY({ ...y, eposta: e.target.value })}
              />
            </label>
          </div>
          <div style={{ display: 'flex', gap: 16, marginBottom: 10, flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 0 }}>
              <input
                type="checkbox"
                checked={Boolean(y.portal)}
                onChange={(e) => setY({ ...y, portal: e.target.checked })}
                style={{ width: 18 }}
              />
              Veli portalına girebilsin
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 0 }}>
              <input
                type="checkbox"
                checked={Boolean(y.yasalTemsilci)}
                onChange={(e) => setY({ ...y, yasalTemsilci: e.target.checked })}
                style={{ width: 18 }}
              />
              Yasal temsilci
            </label>
          </div>
          <p className="alt" style={{ marginBottom: 10 }}>
            Portal erişimi tek başına yetmez: veli portalı onamı da alınmalı. Telefon ya da
            e-posta girilmezse veli kendi başına giriş yapamaz, bağlantıyla girer.
          </p>
          <div className="dugmeler">
            <button className="birincil" type="submit" disabled={bekliyor}>
              {bekliyor ? 'Ekleniyor…' : 'Ekle'}
            </button>
            <button type="button" onClick={() => setFormAcik(false)}>
              Vazgeç
            </button>
          </div>
        </form>
      )}
    </div>
  )
}

/**
 * Seanslar ve notları.
 *
 * "Not yok" ile "not kilitli" ayrı gösteriliyor: ikisi klinik olarak
 * farklı durumlar. Yazılmamış not bir eksik, kilitli not bitmiş bir
 * kayıt.
 */
function Seanslar({
  danisanId,
  yazabilir,
  onAc,
}: {
  danisanId: string
  yazabilir: boolean
  onAc: (seansId: string) => void
}) {
  const [liste, setListe] = useState<SeansOzet[] | null>(null)
  const [tur, setTur] = useState('followup')
  const [hata, setHata] = useState<string | null>(null)
  const [bekliyor, setBekliyor] = useState(false)

  const yukle = useCallback(() => {
    api
      .seanslar(danisanId)
      .then((r) => setListe(r.seanslar))
      .catch(() => setListe([]))
  }, [danisanId])

  useEffect(() => {
    yukle()
  }, [yukle])

  const ac = async () => {
    setHata(null)
    setBekliyor(true)
    try {
      const r = await api.seansAc({ danisanId, tur })
      onAc(r.seans.id)
    } catch {
      setHata('Seans açılamadı.')
    } finally {
      setBekliyor(false)
    }
  }

  if (liste === null) return null

  return (
    <>
      <h2>Seanslar</h2>
      {hata && <div className="uyari">{hata}</div>}

      {yazabilir && (
        <div className="dugmeler" style={{ marginBottom: 12 }}>
          <select value={tur} onChange={(e) => setTur(e.target.value)} style={{ width: 200 }}>
            {Object.entries(SEANS_TURU).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
          <button className="birincil" disabled={bekliyor} onClick={() => void ac()}>
            {bekliyor ? 'Açılıyor…' : 'Seans aç'}
          </button>
        </div>
      )}

      {liste.length === 0 ? (
        <div className="bilgi">Bu dosyada henüz seans kaydı yok.</div>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Tarih</th>
              <th>Tür</th>
              <th>Uygulayıcı</th>
              <th>Not</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {liste.map((s) => (
              <tr key={s.id} className="tiklanir" onClick={() => onAc(s.id)}>
                <td>{tarih(s.baslangic)}</td>
                <td>{SEANS_TURU[s.tur] ?? s.tur}</td>
                <td className="alt" style={{ margin: 0 }}>
                  {s.uygulayici ?? '—'}
                </td>
                <td>
                  <span
                    className={'rozet' + (s.kilitli ? ' ok' : s.notId ? ' warn' : ' bad')}
                  >
                    {s.kilitli ? 'Kilitli' : s.notId ? 'Taslak' : 'Not yok'}
                  </span>
                </td>
                <td style={{ textAlign: 'right' }}>
                  <button onClick={() => onAc(s.id)}>{s.kilitli ? 'Gör' : 'Yaz'}</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  )
}

/**
 * Ölçüme hangi görevlerin gireceği.
 *
 * Varsayılan tam set. Uygulayıcı kısa bir takip ölçümü göndermek
 * isteyebiliyor — "sadece kutu sırası" ya da "kutular ve oklar" gibi.
 * Seçim ölçümün kaydına yazılıyor; yoksa eksik set tam set sanılır ve
 * "bu ölçümde dürtü kontrolü neden yok" sorusu cevapsız kalır.
 */
function GorevSecimi({
  gorevler,
  secili,
  onYukle,
  onDegis,
}: {
  gorevler: GorevSatiri[] | null
  secili: Set<string>
  onYukle: (g: GorevSatiri[]) => void
  onDegis: (s: Set<string>) => void
}) {
  useEffect(() => {
    if (gorevler) return
    api
      .gorevler()
      .then((r) => onYukle(r.gorevler))
      .catch(() => onYukle([]))
  }, [gorevler, onYukle])

  if (!gorevler || gorevler.length === 0) return null

  const tumu = secili.size === 0
  const toplamSn = tumu
    ? gorevler.reduce((a, g) => a + g.saniye, 0)
    : gorevler.filter((g) => secili.has(g.kod)).reduce((a, g) => a + g.saniye, 0)

  const degis = (kod: string) => {
    const y = new Set(secili)
    if (y.has(kod)) y.delete(kod)
    else y.add(kod)
    onDegis(y)
  }

  return (
    <div style={{ borderTop: '1px solid var(--line)', paddingTop: 12, marginBottom: 12 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
        <strong style={{ fontSize: 13, color: 'var(--muted)' }}>Hangi görevler</strong>
        <span className="alt" style={{ margin: 0 }}>
          {tumu ? 'tümü' : `${secili.size} görev`} · yaklaşık{' '}
          {Math.max(1, Math.round(toplamSn / 60))} dakika
        </span>
        {!tumu && (
          <button type="button" onClick={() => onDegis(new Set())}>
            Tümünü seç
          </button>
        )}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
        {gorevler.map((g) => {
          const isaretli = tumu || secili.has(g.kod)
          return (
            <button
              key={g.kod}
              type="button"
              onClick={() => degis(g.kod)}
              className={secili.has(g.kod) ? 'birincil' : ''}
              style={{ opacity: isaretli ? 1 : 0.55 }}
              title={`${g.denemeSayisi} deneme · ${Math.round(g.saniye / 60)} dk`}
            >
              {g.ad.replace(' oyunu', '')}
            </button>
          )
        })}
      </div>
      <p className="alt" style={{ margin: '8px 0 0' }}>
        Hiçbiri seçilmezse tam set gönderilir. Kısa set gelişim takibinde işe yarar ama
        seçilmeyen alanlar o ölçümde ölçülmez.
      </p>
    </div>
  )
}

/**
 * Ödev listesi.
 *
 * Program haftası, yapılma durumu ve doğruluk oranı. Oran tek başına az
 * şey anlatıyor; satıra tıklayınca madde madde detay açılıyor.
 */
function Odevler({
  danisanId,
  onAc,
  yazabilir,
  onProgram,
  onOdevAta,
}: {
  danisanId: string
  onAc: (odevId: string) => void
  yazabilir: boolean
  onProgram: () => void
  onOdevAta: () => void
}) {
  const [liste, setListe] = useState<OdevOzet[] | null>(null)
  const [kaldirilan, setKaldirilan] = useState<string | null>(null)

  const yukle = useCallback(() => {
    api
      .odevler(danisanId)
      .then((r) => setListe(r.odevler))
      .catch(() => setListe([]))
  }, [danisanId])

  useEffect(() => {
    yukle()
  }, [yukle])

  const kaldir = async (id: string) => {
    setKaldirilan(id)
    try {
      await api.odevKaldir(id)
      yukle()
    } catch {
      /* yapılmış ödev kaldırılamaz; liste değişmez */
    } finally {
      setKaldirilan(null)
    }
  }

  const dugme = yazabilir ? (
    <div className="dugmeler" style={{ marginBottom: 12 }}>
      <button className="birincil" onClick={onOdevAta}>
        Ödev ata
      </button>
      <button onClick={onProgram}>Program oluştur</button>
    </div>
  ) : null

  if (liste === null) return null
  if (liste.length === 0) {
    return (
      <>
        <h2>Ödevler</h2>
        {dugme}
        <div className="bilgi">
          Bu dosyada tanımlı egzersiz ödevi yok. Program oluşturulduğunda haftalık setler
          burada görünür.
        </div>
      </>
    )
  }

  return (
    <>
      <h2>Ödevler</h2>
      {dugme}
      {/*
        Tablet haftaları: TEK KOD, bütün hafta.
        Senaryo şu — uygulayıcı programı attı, çocuk haftaya tabletiyle
        geldi. Ödev başına bağlantı üretince her egzersiz için ayrı kod
        okutmak gerekiyordu; dört egzersizlik bir hafta dört okutma
        demekti ve pratikte uygulayıcı tableti alıp kendi açıyordu.
      */}
      <HaftaKodu
        haftalar={[
          ...new Map(
            liste.filter((o) => o.kanal !== 'print').map((o) => [o.haftaId, o]),
          ).values(),
        ].map((o) => ({ id: o.haftaId, no: o.hafta }))}
      />

      {/*
        Kâğıt haftaların çıktısı hafta bazında alınıyor: çocuğa tek tek
        sayfa değil, o haftanın seti veriliyor. Cevap anahtarı ayrı bir
        bağlantı — çocuğa verilen çıktıda bulunmamalı.
      */}
      {[...new Map(liste.filter((o) => o.kanal !== 'digital').map((o) => [o.haftaId, o])).values()]
        .length > 0 && (
        <div className="kart">
          <strong style={{ fontSize: 13, color: 'var(--muted)' }}>Kâğıt çıktı</strong>
          <div className="dugmeler">
            {[
              ...new Map(
                liste.filter((o) => o.kanal !== 'digital').map((o) => [o.haftaId, o]),
              ).values(),
            ].map((o) => (
              <span key={o.haftaId} style={{ display: 'inline-flex', gap: 6 }}>
                <a href={haftaBaskiAdresi(o.haftaId)} target="_blank" rel="noreferrer">
                  <button type="button">{o.hafta}. hafta</button>
                </a>
                <a href={haftaBaskiAdresi(o.haftaId, true)} target="_blank" rel="noreferrer">
                  <button type="button" title="Yalnızca uygulayıcı için">
                    + anahtar
                  </button>
                </a>
              </span>
            ))}
          </div>
        </div>
      )}

      <table>
        <thead>
          <tr>
            <th>Hafta</th>
            <th>Egzersiz</th>
            <th>Kanal</th>
            <th>Durum</th>
            <th>Sonuç</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {liste.map((o) => (
            <tr key={o.id} className="tiklanir" onClick={() => onAc(o.id)}>
              <td>{o.hafta}</td>
              <td>{egzersizAdi(o.kod)}</td>
              <td className="alt" style={{ margin: 0 }}>
                {o.kanal === 'digital' ? 'tablet' : o.kanal === 'print' ? 'kâğıt' : 'ikisi de'}
              </td>
              <td>
                <span className={'rozet' + (o.yapildi ? ' ok' : '')}>
                  {o.yapildi ? 'Yapıldı' : 'Bekliyor'}
                </span>
              </td>
              <td>
                {o.yapildi && o.toplam
                  ? `${o.dogru ?? 0}/${o.toplam}`
                  : o.yapildi
                    ? 'elle işaretlendi'
                    : '—'}
              </td>
              <td style={{ textAlign: 'right' }}>
                <button onClick={() => onAc(o.id)}>Detay</button>
                {yazabilir && !o.yapildi && (
                  <button
                    style={{ marginLeft: 6 }}
                    disabled={kaldirilan === o.id}
                    onClick={(e) => {
                      e.stopPropagation()
                      void kaldir(o.id)
                    }}
                  >
                    Kaldır
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  )
}

/**
 * Dönem varsayılanı son 30 gün.
 *
 * Program haftaları çoğu merkezde aylık görüşmeye denk geliyor; her
 * seferinde iki tarih doldurtmak gereksiz. Yine de düzenlenebilir
 * bırakıldı, çünkü "şu iki ölçüm arasını raporla" sık bir istek.
 */
function sonOtuzGun(): { bas: string; bit: string } {
  const bit = new Date()
  const bas = new Date(bit.getTime() - 30 * 24 * 3600 * 1000)
  const yaz = (d: Date) => d.toISOString().slice(0, 10)
  return { bas: yaz(bas), bit: yaz(bit) }
}

function Raporlar({
  danisanId,
  yazabilir,
  onAc,
}: {
  danisanId: string
  yazabilir: boolean
  onAc: (raporId: string) => void
}) {
  const varsayilan = sonOtuzGun()
  const [liste, setListe] = useState<RaporOzet[] | null>(null)
  const [formAcik, setFormAcik] = useState(false)
  const [hedef, setHedef] = useState<RaporHedefi>('parent')
  const [bas, setBas] = useState(varsayilan.bas)
  const [bit, setBit] = useState(varsayilan.bit)
  const [hata, setHata] = useState<string | null>(null)
  const [bekliyor, setBekliyor] = useState(false)

  const yukle = useCallback(async () => {
    try {
      const r = await api.raporlar(danisanId)
      setListe(r.raporlar)
    } catch {
      setListe([])
    }
  }, [danisanId])

  useEffect(() => {
    void yukle()
  }, [yukle])

  const uret = async () => {
    setHata(null)
    setBekliyor(true)
    try {
      const r = await api.raporUret(danisanId, hedef, bas, bit)
      setFormAcik(false)
      await yukle()
      onAc(r.rapor.id)
    } catch (e) {
      setHata(
        e instanceof ApiHatasi && e.kod === 'donem_ters'
          ? 'Dönem başlangıcı bitişten sonra olamaz.'
          : 'Rapor üretilemedi.',
      )
    } finally {
      setBekliyor(false)
    }
  }

  return (
    <>
      <h2>Raporlar</h2>
      {hata && <div className="uyari">{hata}</div>}

      {yazabilir && (
        <div className="dugmeler" style={{ marginBottom: 12 }}>
          <button className="birincil" onClick={() => setFormAcik((v) => !v)}>
            {formAcik ? 'Vazgeç' : 'Yeni rapor'}
          </button>
        </div>
      )}

      {formAcik && (
        <div className="kart">
          <div className="satir">
            <label>
              Kime
              <select value={hedef} onChange={(e) => setHedef(e.target.value as RaporHedefi)}>
                <option value="parent">Veli — sade dil, sayı yok</option>
                <option value="clinician">Klinik — dosya içi teknik özet</option>
              </select>
            </label>
            <label>
              Dönem başı
              <input type="date" value={bas} onChange={(e) => setBas(e.target.value)} />
            </label>
            <label>
              Dönem sonu
              <input type="date" value={bit} onChange={(e) => setBit(e.target.value)} />
            </label>
          </div>
          <div className="dugmeler">
            <button className="birincil" onClick={() => void uret()} disabled={bekliyor}>
              {bekliyor ? 'Üretiliyor…' : 'Taslağı üret'}
            </button>
          </div>
          <p className="alt" style={{ margin: '10px 0 0' }}>
            Taslak üretilir; okuyup düzelttikten sonra siz göndereceksiniz.
          </p>
        </div>
      )}

      {liste === null ? (
        <p className="alt">Yükleniyor…</p>
      ) : liste.length === 0 ? (
        <div className="bilgi">Bu dosyada henüz rapor yok.</div>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Dönem</th>
              <th>Kime</th>
              <th>Durum</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {liste.map((r) => (
              <tr key={r.id}>
                <td>
                  {gun(r.donemBas)} – {gun(r.donemBit)}
                </td>
                <td>{HEDEF_ADI[r.hedef] ?? r.hedef}</td>
                <td>
                  <span className={'rozet' + (r.gonderildi ? ' ok' : ' warn')}>
                    {r.gonderildi ? 'Gönderildi' : 'Taslak'}
                  </span>
                </td>
                <td style={{ textAlign: 'right' }}>
                  <button onClick={() => onAc(r.id)}>{r.gonderildi ? 'Gör' : 'Düzenle'}</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  )
}

/* ------------------------------------------------------------------ */

function Onamlar({
  dosya,
  onam,
  yazabilir,
  danisanId,
  onDegisti,
}: {
  dosya: DanisanDosyasi
  onam: OnamDurumu
  yazabilir: boolean
  danisanId: string
  onDegisti: () => void
}) {
  const [acilan, setAcilan] = useState<string | null>(null)
  const [yontem, setYontem] = useState<YeniOnam['yontem']>('wet_signature')
  const [verenKisiId, setVeren] = useState('')
  const [hata, setHata] = useState<string | null>(null)
  const [bekliyor, setBekliyor] = useState(false)

  const kisiler = dosya.ilgiliKisiler as Record<string, string>[]
  const verilen = new Set(dosya.onamlar.map((o) => o.kapsam))

  const kaydet = async (kapsam: string) => {
    const belge = BELGE[kapsam]
    if (!belge) return
    setHata(null)
    setBekliyor(true)
    try {
      await api.onamEkle(danisanId, {
        kapsam: kapsam as YeniOnam['kapsam'],
        belgeKodu: belge.kod,
        belgeSurumu: belge.surum,
        yontem,
        verenKisiId: verenKisiId || undefined,
        kendiVerdi: !verenKisiId,
      })
      setAcilan(null)
      setVeren('')
      onDegisti()
    } catch (e) {
      setHata(
        e instanceof ApiHatasi && e.durum === 403
          ? 'Onam kaydetme yetkiniz yok.'
          : 'Onam kaydedilemedi.',
      )
    } finally {
      setBekliyor(false)
    }
  }

  return (
    <div className="kart">
      <h2 style={{ margin: '0 0 4px' }}>Onamlar</h2>
      <p className="alt" style={{ marginBottom: 12 }}>
        Aydınlatma ve ölçüm onamı olmadan sistem ölçüm başlatmıyor. Islak imzalı belgeyi
        dosyaya koyduktan sonra buradan işaretleyin.
      </p>
      {hata && <div className="uyari">{hata}</div>}

      {(
        ['data_processing', 'assessment', 'program', 'parent_portal', 'school_sharing'] as const
      ).map((k) => {
        const kayit = dosya.onamlar.find((o) => o.kapsam === k)
        return (
          <div
            key={k}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '8px 0',
              borderTop: '1px solid var(--line)',
            }}
          >
            <span className={'rozet' + (verilen.has(k) ? ' ok' : '')}>
              {verilen.has(k) ? 'Alındı' : 'Yok'}
            </span>
            <span style={{ flex: 1 }}>
              {KAPSAM_ADI[k]}
              {kayit && (
                <>
                  {' · '}
                  <OnamKaniti
                    onamId={kayit.id}
                    danisanId={danisanId}
                    mevcutEkId={kayit.kanitEkId}
                    yazabilir={yazabilir}
                    onDegisti={onDegisti}
                  />
                </>
              )}
            </span>
            {yazabilir && !verilen.has(k) && (
              <button onClick={() => setAcilan(acilan === k ? null : k)}>
                {acilan === k ? 'Vazgeç' : 'Onamı kaydet'}
              </button>
            )}
            {yazabilir && verilen.has(k) && kayit && (
              <button
                onClick={() => {
                  setHata(null)
                  void api
                    .onamGeriCek(kayit.id)
                    .then(onDegisti)
                    .catch(() => setHata('Onam geri çekilemedi.'))
                }}
              >
                Geri çek
              </button>
            )}
          </div>
        )
      })}

      {acilan && (
        <div style={{ borderTop: '1px solid var(--line)', paddingTop: 12, marginTop: 4 }}>
          <strong style={{ fontSize: 14 }}>{KAPSAM_ADI[acilan]} onamı</strong>
          <p className="alt" style={{ margin: '4px 0 10px' }}>
            Belge: {BELGE[acilan]?.kod} s{BELGE[acilan]?.surum}
          </p>
          <div className="satir">
            <label>
              Alınma biçimi
              <select value={yontem} onChange={(e) => setYontem(e.target.value as never)}>
                <option value="wet_signature">Islak imza</option>
                <option value="touch_signature">Ekranda imza</option>
                <option value="sms_code">SMS kodu</option>
              </select>
            </label>
            {kisiler.length > 0 && (
              <label>
                Onamı veren
                <select value={verenKisiId} onChange={(e) => setVeren(e.target.value)}>
                  <option value="">Danışanın kendisi</option>
                  {kisiler.map((k) => (
                    <option key={k['id']} value={k['id']}>
                      {k['firstName']} {k['lastName']}
                      {k['relation'] ? ` (${k['relation']})` : ''}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>
          <div className="dugmeler">
            <button className="birincil" disabled={bekliyor} onClick={() => void kaydet(acilan)}>
              {bekliyor ? 'Kaydediliyor…' : 'Kaydet'}
            </button>
          </div>
        </div>
      )}

      {!onam.olcumBaslatilabilir && (
        <div className="bilgi" style={{ marginTop: 12, marginBottom: 0 }}>
          Ölçüm başlatmak için <strong>aydınlatma</strong> ve <strong>ölçüm</strong> onamlarının
          ikisi de gerekli.
        </div>
      )}
    </div>
  )
}

/* ------------------------------------------------------------------ */

function OlcumBaslat({
  danisanId,
  acik,
  yazabilir,
  onBasladi,
}: {
  danisanId: string
  acik: boolean
  yazabilir: boolean
  onBasladi: () => void
}) {
  const [form, setForm] = useState<'A' | 'B' | 'C'>('A')
  const [gorevler, setGorevler] = useState<GorevSatiri[] | null>(null)
  const [secili, setSecili] = useState<Set<string>>(new Set())
  const [baglanti, setBaglanti] = useState<{
    adres: string
    dakika: number
    olcumDakika: number
  } | null>(null)
  const [hata, setHata] = useState<string | null>(null)
  const [bekliyor, setBekliyor] = useState(false)

  const basla = async () => {
    setHata(null)
    setBekliyor(true)
    try {
      // Hiç seçilmediyse tam set: boş liste göndermek çocuğu boş
      // ekranla bırakırdı
      const r = await api.olcumBaslat(
        danisanId,
        form,
        secili.size > 0 ? [...secili] : undefined,
      )
      setBaglanti({
        adres: oynaticiAdresi(r.oynatma.jeton),
        dakika: Math.round(r.oynatma.gecerlilikSn / 60),
        olcumDakika: r.olcum.tahminiDakika,
      })
      onBasladi()
    } catch (e) {
      if (e instanceof ApiHatasi && e.kod === 'onam_yok') {
        setHata('Onam eksik görünüyor; yukarıdaki onamları kaydedip tekrar deneyin.')
      } else {
        setHata('Ölçüm başlatılamadı.')
      }
    } finally {
      setBekliyor(false)
    }
  }

  return (
    <div className="kart">
      <h2 style={{ margin: '0 0 4px' }}>Ölçüm başlat</h2>
      <p className="alt" style={{ marginBottom: 12 }}>
        Başlattığınızda çocuğun cihazında açılacak tek kullanımlık bir bağlantı üretilir.
        Çocuk hesap açmaz, parola girmez; bağlantı yalnızca bu ölçüme geçerlidir.
      </p>
      {hata && <div className="uyari">{hata}</div>}

      <div className="satir">
        <label>
          Form
          <select value={form} onChange={(e) => setForm(e.target.value as never)}>
            <option value="A">A — ilk ölçüm</option>
            <option value="B">B — tekrar ölçüm</option>
            <option value="C">C — üçüncü ölçüm</option>
          </select>
        </label>
      </div>

      <GorevSecimi
        gorevler={gorevler}
        secili={secili}
        onYukle={setGorevler}
        onDegis={setSecili}
      />
      <div className="dugmeler">
        <button className="birincil" disabled={!acik || bekliyor} onClick={() => void basla()}>
          {bekliyor ? 'Başlatılıyor…' : 'Ölçümü başlat'}
        </button>
      </div>

      {baglanti && (
        <>
          <Baglanti
            adres={baglanti.adres}
            kare
            mesaj="Çocuğunuzun ölçümü için bağlantı. Tabletten açıp sessiz bir ortamda başlayın."
            not={`Bağlantı ${baglanti.dakika} dakika geçerli ve ölçüm bitince kapanır. Seçtiğiniz görevler yaklaşık ${baglanti.olcumDakika} dakika sürüyor; çocuk tabletini sessize alıp başlatın.`}
          />
          <div className="dugmeler">
            <a href={baglanti.adres} target="_blank" rel="noreferrer">
              <button type="button">Bu cihazda aç</button>
            </a>
          </div>
        </>
      )}
    </div>
  )
}

/* ------------------------------------------------------------------ */

/**
 * Haftanın tablet kodu.
 *
 * Tek kod bütün haftayı açıyor; çocuk listeden seçiyor. Kod bir
 * DİZİN bağlantısı: tek başına egzersiz açmıyor, egzersizi hâlâ
 * ödev başına üretilen jeton açıyor (bkz. api/routes/exercises.ts).
 */
function HaftaKodu({ haftalar }: { haftalar: { id: string; no: number }[] }) {
  const [acik, setAcik] = useState<{ no: number; adres: string; saat: number } | null>(null)
  const [bekliyor, setBekliyor] = useState<string | null>(null)
  const [hata, setHata] = useState<string | null>(null)

  if (haftalar.length === 0) return null

  const uret = async (h: { id: string; no: number }) => {
    setHata(null)
    setBekliyor(h.id)
    try {
      const r = await haftaApi.baglanti(h.id)
      setAcik({ no: r.hafta, adres: haftaAdresi(r.jeton), saat: Math.round(r.gecerlilikSn / 3600) })
    } catch (e) {
      setHata(
        e instanceof ApiHatasi && e.kod === 'dijital_odev_yok'
          ? 'Bu haftada tablet ödevi yok; yalnızca kâğıt set var.'
          : 'Bağlantı üretilemedi.',
      )
    } finally {
      setBekliyor(null)
    }
  }

  return (
    <div className="kart">
      <strong style={{ fontSize: 13, color: 'var(--muted)' }}>Tablet — haftanın kodu</strong>
      <p className="alt" style={{ margin: '4px 0 8px' }}>
        Tek kod o haftanın bütün tablet egzersizlerini açıyor; çocuk listeden sırayla yapıyor.
      </p>
      {hata && <div className="uyari">{hata}</div>}
      <div className="dugmeler">
        {haftalar.map((h) => (
          <button key={h.id} type="button" onClick={() => void uret(h)} disabled={bekliyor === h.id}>
            {bekliyor === h.id ? 'Üretiliyor…' : `${h.no}. hafta kodu`}
          </button>
        ))}
      </div>
      {acik && (
        <div style={{ marginTop: 10 }}>
          <Baglanti
            adres={acik.adres}
            kare
            mesaj={`${acik.no}. haftanın çalışmaları. Tabletten açıp sessiz bir ortamda yapabilir.`}
            not={`Bağlantı ${acik.saat} saat geçerli. Haftanın bütün tablet egzersizleri bu koddan açılıyor; biten çalışma listede işaretli görünüyor.`}
          />
        </div>
      )}
    </div>
  )
}
